import { DuckSelector, createSelector, createCachedSelector, createDeepEqualSelector } from '@atc/modular-redux';
import { calculateDownPayment, getLowestPersonalizedPayment, getPersonalizedPaymentByLender, getObfuscatedCredit } from '@atc/wallet-payment-fns';
import _get from 'lodash/get';
import { LruObjectCache } from 're-reselect';
import { storageKeys } from './storageValues';
const DEFAULT_EMPTY_DEAL = {};
const DEFAULT_EMPTY_PAYMENT = {};
export default duck => ({
  getState: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState)),
  _getPaymentsInfo: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => {
    const storedValues = storageKeys.reduce((acc, key) => {
      acc[key] = localState[key];
      return acc;
    }, {});
    return storedValues;
  })),
  getPaymentsInfo: new DuckSelector(selectors => createDeepEqualSelector(
  // Because _getPaymentsInfo returns a new object every time state changes, due to using reduce, we shadow it
  // with this and use a deepEqualsSelector.  We should really get rid of this selector entirely because it is
  // just taking a bunch of state that should be selected with individual selectors and mashing it together in a
  // new object.
  selectors._getPaymentsInfo, localState => localState)),
  getBudget: new DuckSelector(selectors => state => selectors.getDuckState(state).budget),
  getCreditType: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => getObfuscatedCredit(localState.creditType))),
  getCreatedDate: new DuckSelector(selectors => state => selectors.getDuckState(state).createdDate),
  getDownPayment: new DuckSelector(selectors => state => selectors.getDuckState(state).downPayment),
  getDownPaymentPercent: new DuckSelector(selectors => state => selectors.getDuckState(state).downPaymentPercent),
  getDownPaymentType: new DuckSelector(selectors => state => selectors.getDuckState(state).downPaymentType),
  getLeaseTerm: new DuckSelector(selectors => state => selectors.getDuckState(state).leaseTerm),
  getLoanTerm: new DuckSelector(selectors => state => selectors.getDuckState(state).loanTerm),
  getMonthlyBudget: new DuckSelector(selectors => state => selectors.getDuckState(state).monthlyBudget),
  getPaymentOption: new DuckSelector(selectors => state => selectors.getDuckState(state).paymentOption),
  getTradeIn: new DuckSelector(selectors => state => selectors.getDuckState(state).tradeIn),
  // Deal Related Selectors
  getCreditAppDeals: new DuckSelector(selectors => state => selectors.getDuckState(state).creditAppDeals),
  getCreditAppDealsByOwnerId: new DuckSelector(selectors => createCachedSelector(selectors.getCreditAppDeals, (_, ownerId) => ownerId, (creditAppDeals, ownerId) => {
    if (ownerId) {
      return creditAppDeals?.find(deal => deal?.dealId?.length && ownerId === deal?.ownerId);
    }
    return DEFAULT_EMPTY_DEAL;
  })({
    keySelector: (state, ownerId) => ownerId || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getDealDetails: new DuckSelector(selectors => state => selectors.getDuckState(state).dealDetails),
  getHasDeals: new DuckSelector(selectors => createSelector(selectors.getDealDetails, dealDetails => dealDetails && Object.keys(dealDetails).length > 0)),
  getHideReceipt: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.interactions.hideReceipt)),
  getHideReceiptByListingId: new DuckSelector(selectors => createCachedSelector(selectors.getDuckState, (state, id) => id, function (localState, id) {
    if (localState === void 0) {
      localState = {};
    }
    if (id === void 0) {
      id = '';
    }
    return localState.interactions?.hideReceipt?.includes(id);
  })({
    keySelector: (state, id) => id || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getDealByListingVin: new DuckSelector(selectors => createCachedSelector(selectors.getDealDetails, (state, vin) => vin, function (dealDetails, vin) {
    if (dealDetails === void 0) {
      dealDetails = {};
    }
    const matchingDealId = Object.keys(dealDetails).filter(dealId => dealDetails[dealId].vehicle.vin === vin)[0];
    return dealDetails[matchingDealId];
  })({
    keySelector: (state, vin) => vin || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getTransformedDealDetails: new DuckSelector(selectors => createSelector(selectors.getDealDetails, selectors.getHasDeals, function (dealDetails, hasDeals) {
    if (dealDetails === void 0) {
      dealDetails = {};
    }
    if (!hasDeals && Object.keys(dealDetails).length === 0) {
      // returning the same exact deal object instead of a new {} each time this selector runs will prevent
      // unnecessary rendering
      return DEFAULT_EMPTY_DEAL;
    }
    return Object.entries(dealDetails).reduce((acc, _ref) => {
      let [dealXgId, dealDetail = {}] = _ref;
      const {
        vehicle = {}
      } = dealDetail;
      const {
        condition = '',
        make,
        model,
        year,
        trim
      } = vehicle;
      if (dealDetails[dealXgId]) {
        const title = [condition?.toLowerCase() === 'certifiedpreowned' ? 'Certified' : condition, year, make?.name ?? make, model?.name ?? model, trim?.name ?? trim].join(' ');
        acc[dealXgId] = {
          ...dealDetails[dealXgId],
          vehicle: {
            ...vehicle,
            title
          }
        };
      }
      return acc;
    }, {});
  })),
  getDealNotifications: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.dealNotifications)),
  getCreditVerification: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.creditVerification)),
  // Other selectors
  getICOTradeIn: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.icoTradeIn)),
  getMyWalletInteractions: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.interactions)),
  getMyWalletInteraction: new DuckSelector(selectors => createCachedSelector(selectors.getMyWalletInteractions, (state, interaction) => interaction, function (myWalletInteractions, interaction) {
    if (myWalletInteractions === void 0) {
      myWalletInteractions = {};
    }
    return _get(myWalletInteractions, interaction, false);
  })((state, interaction) => interaction || 'default')),
  getMyWallet: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.myWallet)),
  getDisplayMyWalletModal: new DuckSelector(selectors => createSelector(selectors.getMyWallet, myWalletData => _get(myWalletData, 'displayModal'))),
  getListingPayments: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.listingPayments)),
  getListingIncentives: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.listingIncentives)),
  getListingLeasePayments: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.listingLeasePayments)),
  getListingCashPayments: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.listingCashPayments)),
  getPencilingValues: new DuckSelector(selectors => state => selectors.getDuckState(state).penciling),
  getPencilingPayments: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.penciling?.pencilingPayments)),
  getPenciledLatestPageId: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.penciling?.penciledLatestPageId)),
  isPenciledVdpListing: new DuckSelector(selectors => createCachedSelector(selectors.getDuckState, (_, listingId) => listingId, (localState, listingId) => {
    const penciledVdpListings = _get(localState, 'penciling.penciledVdpListings', []);
    return penciledVdpListings.includes(listingId);
  })({
    keySelector: (state, listingId) => listingId || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getLowestPencilingPaymentByListingId: new DuckSelector(selectors => createCachedSelector(selectors.getCreditType, selectors.getPencilingValues, selectors.getLoanTerm, (_, listingId) => listingId, (creditType, penciling, loanTerm, listingId) => {
    if (!listingId || !penciling?.pencilingPayments?.[listingId]) {
      return DEFAULT_EMPTY_PAYMENT;
    }
    const payment = getLowestPersonalizedPayment(penciling?.pencilingPayments?.[listingId], creditType, loanTerm);
    return Object.keys(payment).length ? {
      ...payment,
      creditType,
      term: loanTerm,
      certified: payment.used
    } : DEFAULT_EMPTY_PAYMENT;
  })({
    keySelector: (state, listingId) => listingId || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getCustomLenderPencilingPaymentByListingId: new DuckSelector(selectors => createCachedSelector(selectors.getCreditType, selectors.getPencilingValues, selectors.getLoanTerm, selectors.getLowestPencilingPaymentByListingId, (_, listingId) => listingId, (creditType, penciling, loanTerm, lowestPencilingPayment, listingId) => {
    if (!listingId || !penciling?.pencilingPayments?.[listingId]) {
      return DEFAULT_EMPTY_PAYMENT;
    }
    const customLender = penciling?.customLender || {};
    // default to lowest penciling payment if no custom lender is provided
    if (!customLender?.lender) {
      return lowestPencilingPayment;
    }
    const payment = getPersonalizedPaymentByLender(penciling?.pencilingPayments?.[listingId], creditType, loanTerm, customLender.lender);
    return Object.keys(payment).length ? {
      ...payment,
      creditType,
      term: loanTerm,
      certified: payment.used
    } : DEFAULT_EMPTY_PAYMENT;
  })({
    keySelector: (state, listingId) => listingId || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getLowestCashPaymentByListingId: new DuckSelector(selectors => createCachedSelector(selectors.getCreditType, selectors.getPencilingValues, (_, listingId) => listingId, (creditType, penciling, listingId) => {
    if (!listingId || !penciling?.pencilingPayments?.[listingId]) {
      return DEFAULT_EMPTY_PAYMENT;
    }
    const payment = penciling?.pencilingPayments?.[listingId]?.rates?.[creditType]?.cashOffers?.['-1']?.[0] || {};
    return Object.keys(payment).length ? {
      ...payment
    } : DEFAULT_EMPTY_PAYMENT;
  })({
    keySelector: (state, listingId) => listingId || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getLowestLeasePaymentByListingId: new DuckSelector(selectors => createCachedSelector(selectors.getCreditType, selectors.getPencilingValues, selectors.getLeaseTerm, (_, listingId) => listingId, (creditType, penciling, leaseTerm, listingId) => {
    if (!listingId || !penciling?.pencilingPayments?.[listingId]) {
      return DEFAULT_EMPTY_PAYMENT;
    }
    const payment = penciling?.pencilingPayments?.[listingId]?.rates?.[creditType]?.leaseOffers?.[leaseTerm]?.[0] || {};
    return Object.keys(payment).length ? {
      ...payment
    } : DEFAULT_EMPTY_PAYMENT;
  })({
    keySelector: (state, listingId) => listingId || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getEcommercePaymentsInfo: new DuckSelector(selectors => createCachedSelector(selectors.getDuckState, function (_, forceDownPayment) {
    if (forceDownPayment === void 0) {
      forceDownPayment = false;
    }
    return forceDownPayment;
  }, function (_, __, pricingDetail) {
    if (pricingDetail === void 0) {
      pricingDetail = {};
    }
    return pricingDetail;
  }, (localState, forceDownPayment, pricingDetail) => {
    const {
      creditType,
      dealType,
      downPayment,
      downPaymentPercent,
      tradeIn: {
        ico
      } = {},
      interactions = {},
      leaseMileageRange,
      leaseTerm,
      loanTerm,
      tradeIn = {}
    } = localState;
    const {
      amountOwed,
      value: tradeInValue
    } = tradeIn;
    const currentIco = Array.isArray(ico) && ico.length ? ico.reduce((a, b) => new Date(a.createdDate) > new Date(b.createdDate) ? a : b, []) : {};
    const {
      offerId = '',
      expirationDate = ''
    } = currentIco;
    const expiry = new Date(expirationDate);
    const isIcoCurrent = expiry > Date.now();
    const {
      hasUpdatedNDSCalc = false,
      hasUpdatedWallet = false,
      paymentEmphasis = false
    } = interactions;
    let ecommPaymentsInfo = {};
    if (paymentEmphasis || hasUpdatedNDSCalc || hasUpdatedWallet) {
      ecommPaymentsInfo = {
        mw_ao: amountOwed,
        mw_c: creditType,
        mw_dt: dealType,
        mw_dp: downPayment,
        mw_dpp: downPayment === null ? downPaymentPercent : null,
        mw_ico: isIcoCurrent ? offerId : null,
        mw_t: dealType === 'lease' ? leaseTerm : loanTerm,
        mw_tv: tradeInValue,
        ...(dealType === 'lease' && {
          mw_m: leaseMileageRange
        })
      };
    }
    if (forceDownPayment && pricingDetail) {
      // only from ecomm cta with listing price
      if (ecommPaymentsInfo?.mw_dpp !== null && (ecommPaymentsInfo?.mw_dp === 0 || ecommPaymentsInfo?.mw_dp === null)) {
        // only needed if DP is 0 or null
        const price = pricingDetail?.incentive || pricingDetail?.salePrice || pricingDetail?.msrp || 0;
        ecommPaymentsInfo.mw_dp = calculateDownPayment(price, {
          downPayment: null,
          downPaymentPercent
        });
      }
    }
    return ecommPaymentsInfo;
  })({
    keySelector: (state, pricingDetail) => JSON.stringify(pricingDetail) || 'default',
    cacheObject: new LruObjectCache({
      cacheSize: 100
    })
  })),
  getWalletId: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.walletId)),
  // NDS
  getIsLoading: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.isLoading)),
  // Wallet PaymentsInfo
  getIsPaymentsInfoLoaded: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.isPaymentsInfoLoaded)),
  getPreviousPaymentInfo: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.previousPaymentInfo)),
  getInterestRates: new DuckSelector(selectors => createSelector(selectors.getDuckState, localState => localState.interestRate))
});